import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { FinalisationCheckResult } from '@cpr/shared/finalisation';
import { environment } from 'apps/mba-cpr-survey-portal/src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { TableApiService } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/tables/services/table-api.service';
import { ITableOptions } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/interfaces/table/table-options.interface';
import { openConfirmActionDialog } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ICheckBlock } from '../../check-block/interface/check-block.interface';
import { IMeasurement } from '../../measurement/measurement.interface';
import { ITaxon } from '../../taxon/taxon.interface';
import { IAnalysisSample } from '../interface/analysis-sample.interface';
import { ISample } from '../interface/sample.interface';

@Injectable({ providedIn: 'root' })
export class SamplesService extends TableApiService<ISample> {
  constructor(
    http: HttpClient,
    private readonly dialog: MatDialog,
  ) {
    super(http, `${environment.cprServiceApi}sample/`);
  }

  getPaginated(prefixes: string | string[], options: ITableOptions) {
    return super.getPaginated(prefixes, options);
  }

  clearWithDialog(towId: string, onSuccess?: () => void) {
    openConfirmActionDialog(this.dialog, {
      title: 'Clear samples',
      body:
        'This action will clear the tow cutting points and samples so they can be regenerated.' +
        ' The tow log will be unlocked for further edits after this action.' +
        ' The page will reload once clearing is complete.',
      confirmButtonLabel: 'Clear',
    }).subscribe(async () => {
      await lastValueFrom(
        this.httpClient.post(`${environment.cprServiceApi}sample/clear/${towId}`, null),
      );
      onSuccess?.();
    });
  }

  generateWithDialog(towId: string, onSuccess?: () => void) {
    openConfirmActionDialog(this.dialog, {
      title: 'Generate samples',
      body:
        'This action will generate cutting points and samples from the current tow log information.' +
        ' The tow log will be locked from further edits after this action.',
      confirmButtonLabel: 'Generate',
    }).subscribe(async () => {
      await lastValueFrom(
        this.httpClient.post(`${environment.cprServiceApi}sample/generate/${towId}`, null),
      );
      onSuccess?.();
    });
  }

  analyseMany(samples: Pick<ISample, '_id' | 'chlorophyllIndex' | 'isNotAnalyseable'>[]) {
    return lastValueFrom(
      this.httpClient.patch(`${environment.cprServiceApi}sample/analyse`, { samples }),
    );
  }

  allocateMany(samples: Pick<ISample, '_id' | 'userId'>[]) {
    return lastValueFrom(
      this.httpClient.patch(`${environment.cprServiceApi}sample/allocate`, { samples }),
    );
  }

  updateSampleDetails(id: string, details: Pick<ISample, 'microscopeId' | 'comment'>) {
    return lastValueFrom(
      this.httpClient.patch(`${environment.cprServiceApi}sample/${id}/details`, details),
    );
  }

  checkSubmission(sampleId: string) {
    return lastValueFrom(
      this.httpClient.get<FinalisationCheckResult[]>(
        `${environment.cprServiceApi}sample/${sampleId}/submit`,
      ),
    );
  }

  submit(sampleId: string) {
    return lastValueFrom(
      this.httpClient.post(`${environment.cprServiceApi}sample/${sampleId}/submit`, {}),
    );
  }

  unsubmit(sampleId: string) {
    return lastValueFrom(
      this.httpClient.post(`${environment.cprServiceApi}sample/${sampleId}/unsubmit`, {}),
    );
  }

  checkTow(towId: string) {
    return lastValueFrom(
      this.httpClient.get<Record<number, FinalisationCheckResult[]>>(
        `${environment.cprServiceApi}sample/${towId}/finalise/tow`,
      ),
    );
  }

  finaliseTow(towId: string) {
    return lastValueFrom(
      this.httpClient.post(`${environment.cprServiceApi}sample/${towId}/finalise/tow`, {}),
    );
  }

  getCheckBlocks(sampleID: string) {
    return this.get(`${sampleID}/check-blocks`) as unknown as Promise<ICheckBlock[]>;
  }

  getMeasurements(sampleID: string) {
    return this.get(`${sampleID}/measurements`) as unknown as Promise<
      Array<IMeasurement & { taxon: ITaxon }>
    >;
  }

  putMeasurements(sampleID: string, measurements: IMeasurement[]) {
    return lastValueFrom(
      this.httpClient.put(`${environment.cprServiceApi}sample/${sampleID}/measurements`, {
        measurements,
      }),
    );
  }

  getAnalysis(id: string) {
    return this.get(`${id}/analysis`) as unknown as Promise<IAnalysisSample>;
  }

  // RANDOM ALLOC DISABLED PRIOR TO UAT 2024-10-30

  // autoAllocate(towID: string, samples: string[], callback?: () => void) {
  //   this.dialog.open(ConfirmDialogComponent, {
  //     data: {
  //       onConfirm: () => {
  //         lastValueFrom(
  //           this.httpClient.post(`${environment.cprServiceApi}sample/allocate/${towID}`, {
  //             samples,
  //           }),
  //         ).then(() => callback?.());
  //       },
  //       title: 'Auto Allocate Samples',
  //       body: `
  //         This action will allocate the selected unfinalised samples evenly amongst all available analysts.
  //         Any selected samples that are already allocated will be re-allocated during this process.
  //         Would you like to proceed?
  //       `,
  //     },
  //   });
  // }
}
